import type { FC } from "react";
import type { NextBestAction } from "@/types/chat-parts";
import styled from "@emotion/styled";
import { ArrowRight, FileText, Globe, Link, Phone } from "lucide-react";
import { breakpoints, colors, fonts, radius, spacing } from "../design-tokens";

const StyledActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: min-content;
  gap: ${spacing.md}px;
  width: 100%;
  height: 100%;
  align-items: flex-start;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.md - 1}px) {
    grid-template-columns: 1fr;
  }
`;

const ActionItem = styled.a`
  display: flex;
  align-items: stretch;
  gap: 0;
  background: ${colors.background};
  border-radius: ${radius.md}px;
  padding: 0;
  min-width: 0;
  min-height: 60px;
  word-wrap: break-word;
  cursor: pointer;
  user-select: none;
  text-align: left;
  text-decoration: none;
  transition: all 200ms ease-in-out;
  overflow: hidden;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: ${breakpoints.md - 1}px) {
    border-radius: ${radius.sm}px;
  }
`;

const IconContainer = styled.div`
  width: 64px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.primary};
  padding: ${spacing.lg}px;
  flex-shrink: 0;
`;

const ActionIcon = styled.div`
  width: 24px;
  height: 24px;
  color: ${colors.background};
  fill: ${colors.background};

  svg {
    width: 100%;
    height: 100%;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${spacing.xs}px;
  flex: 1;
  min-width: 0;
  padding: ${spacing.md}px;
`;

const ActionTitle = styled.h4`
  ${fonts.headingFont}
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text};
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  word-break: break-word;
  line-height: 1.4;
`;

const ActionDetail = styled.span`
  font-size: 14px;
  color: ${colors.textSecondary};
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  word-break: break-all;
  line-height: 1.3;
`;

const ArrowContainer = styled.div`
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: ${spacing.lg}px;
`;

const StyledArrow = styled(ArrowRight)`
  width: 100%;
  height: 100%;
  color: ${colors.accent};
`;

const getActionIcon = (url: string) => {
  // Determine icon based on URL or file type
  if (url.includes(".pdf")) {
    return <FileText />;
  } else if (url.includes("http")) {
    return <Globe />;
  } else if (url.includes("tel:")) {
    return <Phone />;
  } else {
    return <Link />;
  }
};

type ActionsGridProps = {
  actions: NextBestAction[];
};

export const ActionsGrid: FC<ActionsGridProps> = ({ actions }) => {
  const actionsToRender = actions.filter((action) => {
    try {
      new URL(action.url);
      return true;
    } catch (error) {
      return false;
    }
  });

  return (
    <StyledActionsGrid>
      {actionsToRender.map((action) => (
        <ActionItem
          key={action.url}
          href={action.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <IconContainer>
            <ActionIcon>{getActionIcon(action.url)}</ActionIcon>
          </IconContainer>
          <ContentContainer>
            <ActionTitle>{action.title}</ActionTitle>
            <ActionDetail>
              {new URL(action.url).hostname || action.url}
            </ActionDetail>
          </ContentContainer>
          <ArrowContainer>
            <StyledArrow />
          </ArrowContainer>
        </ActionItem>
      ))}
    </StyledActionsGrid>
  );
};
